/**
 * Axios Instance Configuration
 * Handles base URL, authentication tokens, and global error handling
 */

import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ApiError } from './types';

// Get API base URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 60000, // 60 seconds - increased to handle slower backend responses
});

/** Public content paths where 401/403 should not trigger redirect to login (view-only access). */
function isPublicContentPath(pathname: string): boolean {
  const p = pathname.replace(/\/$/, '') || '/';
  return (
    p === '/forums' ||
    p.startsWith('/forums/') ||
    p === '/blog' ||
    p.startsWith('/blog/') ||
    p === '/events' ||
    p.startsWith('/events/')
  );
}

// Request interceptor - Attach auth token to requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage
    const token = localStorage.getItem('auth_token');
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally and trigger notification refresh
apiClient.interceptors.response.use(
  (response) => {
    // Trigger notification refresh for endpoints that create notifications
    const url = response.config?.url || '';
    const method = response.config?.method?.toUpperCase() || '';
    
    // List of endpoints that create notifications
    const notificationTriggerEndpoints = [
      '/events/comments', // Event comments
      '/blog/comments', // Blog comments
      '/forum/comments', // Forum comments
      '/messages', // Direct messages
      '/connections', // Connection requests
      '/connections/', // Connection accept/reject (contains /connections/)
      '/events/', // Event likes, registrations
      '/blog/posts/', // Blog likes
      '/forum/threads/', // Forum thread likes
    ];
    
    // Check if this is a POST/PUT that might create a notification
    if ((method === 'POST' || method === 'PUT') && response.status >= 200 && response.status < 300) {
      const shouldRefresh = notificationTriggerEndpoints.some(endpoint => url.includes(endpoint));
      
      if (shouldRefresh) {
        // Dispatch custom event to trigger notification refresh
        // Header component will listen to this event
        window.dispatchEvent(new CustomEvent('notification-trigger'));
      }
    }
    
    // Return response data directly for convenience
    return response;
  },
  async (error: AxiosError) => {
    // Import error logger dynamically to avoid circular dependencies
    const { errorLogger } = await import('../utils/errorLogger');
    
    // Skip logging for expected 403 on connections/user (viewing another user's connections when not connected)
    const url = error.config?.url || '';
    const isConnectionsUser403 = error.response?.status === 403 && url.includes('/connections/user/');
    if (!isConnectionsUser403) {
      errorLogger.logApiError(
        error,
        url || 'unknown',
        error.config?.method?.toUpperCase() || 'GET'
      );
    }

    // Handle network errors
    if (!error.response) {
      // Check if this is a cancelled/aborted request (not a real error)
      if (error.code === 'ERR_CANCELED' || 
          error.code === 'ECONNABORTED' ||
          error.name === 'AbortError' ||
          error.message === 'Request aborted' || 
          error.message?.includes('canceled') ||
          error.message?.includes('aborted')) {
        // Check if it's a timeout vs a cancellation
        // Timeout errors contain "timeout" in the message
        if (error.message?.includes('timeout')) {
          // This is a timeout error, not a cancellation
          const timeoutError: ApiError = {
            message: 'Request timed out. The server is taking too long to respond. Please try again.',
          };
          return Promise.reject(timeoutError);
        }
        // Silently ignore cancelled requests - they're usually from navigation or component unmounting
        // Return a special error that components can check for and ignore
        const abortError = error as any;
        abortError.isAborted = true;
        return Promise.reject(abortError);
      }
      
      // Network error or server unreachable
      const networkError: ApiError = {
        message: errorLogger.getUserFriendlyMessage(error),
      };
      return Promise.reject(networkError);
    }

    const { status, data } = error.response;

    // Handle 401 Unauthorized - Clear auth and redirect to login (except on public content pages)
    if (status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      const pathname = window.location.pathname;
      const onLogin = pathname.includes('/login');
      const onPublicContent = isPublicContentPath(pathname);
      if (!onLogin && !onPublicContent) {
        window.location.href = '/login';
      }
    }

    // Handle 403 Forbidden - Redirect to login if not authenticated (except on public content pages)
    if (status === 403) {
      const requiresVerification = (data as any)?.requires_verification || false;
      if (!localStorage.getItem('auth_token')) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        const pathname = window.location.pathname;
        const onLogin = pathname.includes('/login');
        const onPublicContent = isPublicContentPath(pathname);
        if (!onLogin && !onPublicContent) {
          window.location.href = '/login';
        }
      }
      
      const forbiddenError: ApiError = {
        message: requiresVerification 
          ? ((data as any)?.message || errorLogger.getUserFriendlyMessage(error))
          : errorLogger.getUserFriendlyMessage(error),
        requires_verification: requiresVerification,
        response: error.response,
      };
      return Promise.reject(forbiddenError);
    }

    // Handle 422 Validation errors
    if (status === 422 && data && typeof data === 'object') {
      const validationError: ApiError = {
        message: (data as any).message || 'Validation failed',
        errors: (data as any).errors || {},
        response: error.response,
      };
      return Promise.reject(validationError);
    }

    // Handle other errors (preserve response so callers can read status)
    const apiError: ApiError = {
      message: (data as any)?.message || errorLogger.getUserFriendlyMessage(error),
      errors: (data as any)?.errors,
      response: error.response,
    };

    return Promise.reject(apiError);
  }
);

export default apiClient;

